import { ref, onMounted, onBeforeUnmount, type Ref } from 'vue'
import { PipeDisplayMode, type PipeInterface } from '../composables/usePipe'

const TRIGGER_COUNT = 7
const TRIGGER_WINDOW_MS = 2000
export const CORNER_SIZE_PX = 60

export interface DevToolsHandle {
  isOpen: Ref<boolean>
  clearStateAndReturnHome: () => Promise<void>
}

export function useDevTools(pipe: PipeInterface): DevToolsHandle {
  const isOpen = ref(false)

  let keyCount = 0
  let keyLastAt = 0
  let tapCount = 0
  let tapLastAt = 0

  const handleKeydown = (e: KeyboardEvent) => {
    const isMatch =
      e.code === 'KeyL' &&
      e.ctrlKey &&
      e.altKey &&
      !e.shiftKey &&
      !e.metaKey &&
      !e.getModifierState('AltGraph')
    if (!isMatch) {
      keyCount = 0
      return
    }
    e.preventDefault()
    const now = Date.now()
    if (now - keyLastAt > TRIGGER_WINDOW_MS) keyCount = 0
    keyLastAt = now
    keyCount += 1
    if (keyCount >= TRIGGER_COUNT) {
      keyCount = 0
      isOpen.value = true
    }
  }

  const handlePointerDown = (e: PointerEvent) => {
    // While the panel is open the backdrop owns clicks; skip detection so repeated
    // corner taps don't churn tapCount or steal a backdrop tap via preventDefault.
    if (isOpen.value) return
    const inCorner = e.clientX < CORNER_SIZE_PX && e.clientY < CORNER_SIZE_PX
    if (!inCorner) {
      tapCount = 0
      return
    }
    const now = Date.now()
    if (now - tapLastAt > TRIGGER_WINDOW_MS) tapCount = 0
    tapLastAt = now
    tapCount += 1
    if (tapCount >= TRIGGER_COUNT) {
      tapCount = 0
      e.preventDefault()
      isOpen.value = true
    }
  }

  onMounted(() => {
    if (pipe.currentMode.value !== PipeDisplayMode.STATIC) return
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('pointerdown', handlePointerDown)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('pointerdown', handlePointerDown)
  })

  const clearStateAndReturnHome = async () => {
    // Clear external state first so the trackingProperties re-capture (triggered
    // by pipe:data-cleared below) sees the cleared cookies, not the old ones.
    try {
      window.sessionStorage.clear()
    } catch {}
    try {
      document.cookie.split(';').forEach((cookie) => {
        const name = (cookie.split('=')[0] ?? '').trim()
        if (name) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      })
    } catch {}
    pipe.clearPersistedData()
    // Mirror the clear_all_pipe_data processor pattern — emit pipe:data-cleared
    // so integrations (trackingProperties, GTM) re-capture/reset after the wipe.
    // Without this, trackingProperties stays absent for the rest of the SPA
    // session and checkout reads return undefined.
    pipe.emit('pipe:data-cleared')
    const landingId = (
      pipe.data.value?.latestContent?.content as { landingPageId?: string } | undefined
    )?.landingPageId
    if (landingId) await pipe.navigateToPageId(landingId)
    isOpen.value = false
  }

  return { isOpen, clearStateAndReturnHome }
}
